/**
 * Network connectivity utilities for handling VPN and RPC connection issues
 */

export class NetworkConnectivityHelper {
  private static lastConnectivityCheck = 0;
  private static connectivityCheckInterval = 30000; // 30 seconds
  private static isConnected = true;

  /**
   * Check if we have basic network connectivity
   */
  static async checkConnectivity(): Promise<boolean> {
    const now = Date.now();
    
    // Don't check too frequently
    if (now - this.lastConnectivityCheck < this.connectivityCheckInterval) {
      return this.isConnected;
    }

    try {
      // Try to fetch a simple endpoint
      const response = await fetch('https://api.github.com/zen', {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
        signal: AbortSignal.timeout(5000), // 5 second timeout
      });
      
      this.isConnected = response.ok;
      this.lastConnectivityCheck = now;
      
      return this.isConnected;
    } catch (error) {
      console.warn('Network connectivity check failed:', error);
      this.isConnected = false;
      this.lastConnectivityCheck = now;
      
      return false;
    }
  }

  /**
   * Check if we're likely on a VPN or have RPC connectivity issues
   */
  static async checkRpcConnectivity(provider: any): Promise<boolean> {
    if (!provider) return false;

    try {
      // Try a simple RPC call with timeout
      const blockNumber = await Promise.race([
        provider.getBlockNumber(),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('RPC timeout')), 3000)
        )
      ]);
      
      return typeof blockNumber === 'number' && blockNumber > 0;
    } catch (error: any) {
      console.warn('RPC connectivity check failed:', error.message);
      
      // Check for common VPN/connectivity error patterns
      const errorMessage = error.message?.toLowerCase() || '';
      const isConnectivityIssue = 
        errorMessage.includes('timeout') ||
        errorMessage.includes('network') ||
        errorMessage.includes('connection') ||
        errorMessage.includes('invalid block range') ||
        error.code === -32603 ||
        error.code === -32000;
      
      if (isConnectivityIssue) {
        console.warn('Detected VPN or RPC connectivity issue');
      }
      
      return false;
    }
  }

  /**
   * Get a safe block range for querying events when on VPN
   */
  static async getSafeBlockRange(
    provider: any, 
    maxBlocks = 1000
  ): Promise<{ fromBlock: number; toBlock: number } | null> {
    try {
      const currentBlock = await provider.getBlockNumber();
      const fromBlock = Math.max(0, currentBlock - maxBlocks);
      
      return {
        fromBlock,
        toBlock: currentBlock
      };
    } catch (error) {
      console.error('Failed to get safe block range:', error);
      return null;
    }
  }

  /**
   * Retry an operation with exponential backoff
   */
  static async retryWithBackoff<T>(
    operation: () => Promise<T>,
    maxRetries = 3,
    baseDelay = 1000
  ): Promise<T> {
    let lastError: Error;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error: any) {
        lastError = error;
        
        // Don't retry on certain error types
        if (error.code === -32603 && error.message?.includes('invalid block range')) {
          throw error; // Let the caller handle block range errors
        }

        if (attempt < maxRetries - 1) {
          const delay = baseDelay * Math.pow(2, attempt);
          console.log(`Operation failed, retrying in ${delay}ms... (attempt ${attempt + 1}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    throw lastError!;
  }
}